import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import DashboardNavbar from '../dashboardNavbar';
import { Card, Btn, Tag, TagDot } from '../ui/primitives';
import { useAppContext } from '../../appprovider';
import { ADMIN_TABS } from './adminSectionTabs';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';
import { useConfirm } from '../ui/ConfirmProvider';
import {
  ORG_CATALOG_AUDIENCE,
  applySharingLocally,
  catalogEntries,
  coursesAvailableToAdd,
  fetchOrgCourseCatalog,
  formatSharedAt,
  invalidateInstructorCatalogCache,
  updateOrgCourseSharing,
} from './orgCourseCatalogApi';

const Shell = styled.div`
  min-height: calc(100vh - 32px);
  margin: 0 auto;
  padding: 0 28px 36px;
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontBody};
  @media (max-width: 768px) { padding: 0 14px 28px; }
`;

const Hero = styled.div`
  width: min(1180px, 100%);
  margin: ${({ theme }) => theme.space} auto ${({ theme }) => theme.gap};
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  h2 { margin: 0; color: ${({ theme }) => theme.text}; font-family: ${({ theme }) => theme.fontHead}; font-size: 16px; font-weight: 700; letter-spacing: -.01em; }
  p { margin: 2px 0 0; color: ${({ theme }) => theme.textMuted}; font-size: ${({ theme }) => theme.fsSm}; }
`;

const HeroActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const Wrap = styled.div`
  width: min(1180px, 100%);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.gap};
`;

const CardTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 750;
  color: ${({ theme }) => theme.text};
`;

const CardText = styled.p`
  margin: 0 0 12px;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.5;
`;

const AdminField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 12px;
  label { font-size: ${({ theme }) => theme.fsSm}; font-weight: 650; color: ${({ theme }) => theme.text}; }
  input, select {
    height: ${({ theme }) => theme.controlH};
    padding: 0 12px;
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: ${({ theme }) => theme.rMd};
    background: ${({ theme }) => theme.surface2};
    color: ${({ theme }) => theme.text};
    font-size: ${({ theme }) => theme.fsSm};
    outline: none;
  }
`;

const Hint = styled.p`
  margin: 6px 0 12px;
  font-size: ${({ theme }) => theme.fsMicro};
  color: ${({ theme }) => theme.textSubtle};
  line-height: 1.45;
`;

const StatusNote = styled.div`
  margin-top: 8px;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.45;
`;

const ErrorNote = styled(StatusNote)`
  color: ${({ theme }) => theme.danger};
`;

const SuccessNote = styled(StatusNote)`
  color: ${({ theme }) => theme.success || theme.accent};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.fsSm};
  th, td { text-align: left; padding: 10px 12px; border-bottom: 1px solid ${({ theme }) => theme.border}; }
  th { color: ${({ theme }) => theme.textSubtle}; font-weight: 700; font-size: ${({ theme }) => theme.fsMicro}; text-transform: uppercase; letter-spacing: .05em; }
`;

/**
 * Admin → Course Catalog. An organization owner or admin shares courses their
 * organization already owns with that organization's instructors.
 *
 * Platform-wide publishing stays on System → Publishing. The "All customers"
 * option is visible so the screen matches the design, but it is disabled —
 * wiring it here would let an org admin bypass the super-admin publishing gates.
 */
export default function AdminCourseCatalog() {
  const { entitlements, currentOrg } = useAppContext();
  const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const askConfirmation = useConfirm();
  const courseSelectRef = useRef(null);

  const adminUserId = localStorage.getItem('userId');
  const orgId = currentOrg || entitlements?.orgId || localStorage.getItem('orgId');

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [removingId, setRemovingId] = useState('');

  const available = useMemo(() => coursesAvailableToAdd(courses), [courses]);
  const shared = useMemo(() => catalogEntries(courses), [courses]);

  const loadCatalog = useCallback(async () => {
    if (!adminUserId) {
      setError('Sign in to manage the course catalog.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = await fetchOrgCourseCatalog({ adminUserId, orgId });
      setCourses(payload.courses);
    } catch (err) {
      setCourses([]);
      setError(err.message || 'Could not load the course catalog.');
    } finally {
      setLoading(false);
    }
  }, [adminUserId, orgId]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const focusCoursePicker = () => {
    if (!guardAction('action.admin.catalog.copy')) return;
    courseSelectRef.current?.focus();
  };

  const handleCopy = async () => {
    if (!guardAction('action.admin.catalog.copy')) return;
    if (!selectedCourseId) {
      setError('Select a course to add.');
      setSuccess('');
      return;
    }

    const courseIdToAdd = selectedCourseId;
    const previous = courses;
    const courseName = available.find((course) => course.courseId === courseIdToAdd)?.courseName
      || 'Course';
    setError('');
    setSuccess('');
    setSaving(true);
    setCourses(applySharingLocally(courses, [courseIdToAdd], true));
    setSelectedCourseId('');

    try {
      const result = await updateOrgCourseSharing({
        adminUserId,
        orgId,
        courseIds: [courseIdToAdd],
        shared: true,
      });
      const updated = result?.updatedCourseIds || [];
      if (!updated.includes(courseIdToAdd)) {
        setCourses(previous);
        setSelectedCourseId(courseIdToAdd);
        setError('That course could not be added. It may belong to another organization.');
        return;
      }
      invalidateInstructorCatalogCache();
      setSuccess(`${courseName} is now visible to instructors in this organization.`);
    } catch (err) {
      setCourses(previous);
      setSelectedCourseId(courseIdToAdd);
      setError(err.message || 'Could not add the course to the catalog.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (course) => {
    if (!guardAction('action.admin.catalog.copy')) return;
    const confirmed = await askConfirmation({
      title: 'Remove from catalog?',
      message: `${course.courseName} will no longer appear for instructors in this organization. Their existing copies stay in their workspace.`,
      confirmLabel: 'Remove from catalog',
      variant: 'danger',
      requireTypedConfirm: false,
    });
    if (!confirmed) {
      return;
    }

    const previous = courses;
    setError('');
    setSuccess('');
    setRemovingId(course.courseId);
    setCourses(applySharingLocally(courses, [course.courseId], false));

    try {
      const result = await updateOrgCourseSharing({
        adminUserId,
        orgId,
        courseIds: [course.courseId],
        shared: false,
      });
      const updated = result?.updatedCourseIds || [];
      if (!updated.includes(course.courseId)) {
        setCourses(previous);
        setError('That course could not be removed. Refresh and try again.');
        return;
      }
      invalidateInstructorCatalogCache();
      setSuccess(`${course.courseName} was removed from the catalog.`);
    } catch (err) {
      setCourses(previous);
      setError(err.message || 'Could not remove the course from the catalog.');
    } finally {
      setRemovingId('');
    }
  };

  return (
    <div>
      <Shell>
        <DashboardNavbar menuItems={ADMIN_TABS} />
        <Hero>
          <div>
            <h2>Course catalog</h2>
            <p>
              Copy existing courses from this organization into the enterprise catalog
              so instructors here can reuse them.
            </p>
          </div>
          <HeroActions>
            <Btn $variant="primary" onClick={focusCoursePicker}>
              Copy course to Enterprise Catalog
            </Btn>
          </HeroActions>
        </Hero>
        <Wrap>
          <Card $pad>
            <CardTitle>Add to Enterprise Catalog</CardTitle>
            <CardText>
              Select one of your organization&apos;s courses to share with its instructors.
            </CardText>
            <AdminField>
              <label htmlFor="org-catalog-course">Course</label>
              <select
                id="org-catalog-course"
                ref={courseSelectRef}
                value={selectedCourseId}
                disabled={loading || saving || available.length === 0}
                onChange={(event) => setSelectedCourseId(event.target.value)}
              >
                <option value="">
                  {loading
                    ? 'Loading courses…'
                    : available.length === 0
                      ? 'No courses left to add'
                      : 'Select a course…'}
                </option>
                {available.map((course) => (
                  <option key={course.courseId} value={course.courseId}>
                    {course.courseName}
                    {course.ownerName ? ` — ${course.ownerName}` : ''}
                  </option>
                ))}
              </select>
            </AdminField>
            <AdminField>
              <label htmlFor="org-catalog-audience">Make available to</label>
              <select
                id="org-catalog-audience"
                value={ORG_CATALOG_AUDIENCE}
                onChange={() => {
                  if (!guardAction('action.admin.catalog.selectAudience')) return;
                }}
              >
                <option value="enterprise">My enterprise only</option>
                <option value="all" disabled>All customers (StudyAK only)</option>
              </select>
            </AdminField>
            <Hint>
              This screen shares a course with instructors in your organization.
              Publishing to every customer stays on System → Publishing, which only
              StudyAK super admins can use.
            </Hint>
            <Btn $variant="primary" onClick={handleCopy} disabled={loading || saving}>
              {saving ? 'Copying…' : 'Copy to catalog'}
            </Btn>
            {error && <ErrorNote>{error}</ErrorNote>}
            {success && <SuccessNote>{success}</SuccessNote>}
          </Card>

          <Card $pad>
            <CardTitle>Enterprise catalog</CardTitle>
            <Table>
              <thead>
                <tr>
                  <th>Course</th>
                  <th>Audience</th>
                  <th>Added</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '20px 0', opacity: 0.6 }}>
                      Loading catalog…
                    </td>
                  </tr>
                ) : shared.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '20px 0', opacity: 0.6 }}>
                      No courses in the catalog yet. Add one above to make it visible
                      to instructors in this organization.
                    </td>
                  </tr>
                ) : shared.map((course) => (
                  <tr key={course.courseId}>
                    <td>
                      <div>{course.courseName}</div>
                      {course.ownerName && (
                        <div style={{ opacity: 0.65, fontSize: '12px' }}>{course.ownerName}</div>
                      )}
                    </td>
                    <td>
                      <Tag $tone="accent"><TagDot />My enterprise only</Tag>
                    </td>
                    <td>{formatSharedAt(course.sharedAt)}</td>
                    <td>
                      <Btn
                        $sm
                        onClick={() => handleRemove(course)}
                        disabled={Boolean(removingId) || saving}
                      >
                        {removingId === course.courseId ? 'Removing…' : 'Remove'}
                      </Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </Wrap>
      </Shell>
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </div>
  );
}
